/**
 * AUTH-002 — the session policy, in one place.
 *
 * The app and Supabase Auth follow ONE policy (docs/specs/AUTH-002-session-expiry-alignment.md
 * §5). The alignment rule: while the app considers the user signed in, the access token
 * is always valid, so no request is ever refused for an expired token. The app keeps
 * refreshing for as long as the user is signed in — INCLUDING while idle — and ends the
 * session itself at 15 minutes idle, revoking it on the server while the token is still
 * valid. The database is never the first to say no.
 *
 * D6 (Kyle, 2026-10-11): the idle sign-out is 15 minutes, superseding the 10 minutes set
 * under SEC-003. The access token still lives 10 minutes (Auth setting, 600 s) and is
 * renewed until sign-out, so the token's lifetime and the idle limit are independent.
 * Any future Supabase server-side inactivity timeout must stay LONGER than this value
 * (planned 20 minutes), so the server is only a backstop.
 *
 * Pure constants and helpers: no browser or Next.js imports, so the server routes, the
 * browser runtime and the unit tests all read the same values.
 */

/** No input in ANY tab for this long → the app signs out (D6: 15 min). Compliance setting: change only by decision. */
export const IDLE_TIMEOUT_MS = 15 * 60 * 1000;

/** Before any `/api/*` or Supabase call the token must have at least this much life left. */
export const MIN_TOKEN_LIFE_SECONDS = 60;

/**
 * Long-running requests (CSV exports) ask for more headroom, so the token they carry
 * outlives the whole response even though the server reads pages after it starts.
 */
export const LONG_REQUEST_TOKEN_LIFE_SECONDS = 300;

/**
 * The session tick. Each tick checks idle and keeps the token fresh (the library refreshes
 * when it is within 90 s of expiry). Background tabs are throttled to about one timer a
 * minute, which is still inside that 90 s margin.
 */
export const SESSION_TICK_MS = 15 * 1000;

/** A gap this long between ticks means the machine slept or the tab was frozen: treat as a wake. */
export const WAKE_GAP_MS = 60 * 1000;

/** Writes of the shared last-activity time are throttled to one per this interval. */
export const ACTIVITY_WRITE_THROTTLE_MS = 5 * 1000;

/** Sign-out never waits longer than this for the server revoke; local tokens are cleared regardless. */
export const SIGN_OUT_REVOKE_TIMEOUT_MS = 5 * 1000;

/** First back-off after a 429 on token refresh; doubles on each further 429, capped below. */
export const REFRESH_BACKOFF_INITIAL_MS = 30 * 1000;
export const REFRESH_BACKOFF_MAX_MS = 5 * 60 * 1000;

/** localStorage key holding the last input time across every tab of this origin. */
export const LAST_ACTIVITY_KEY = 'pugmed.auth.lastActivity';

/** Input events that count as activity. */
export const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'click',
] as const;

/** Machine-readable code on every 401 caused by an expired, invalid or revoked session. */
export const SESSION_EXPIRED_CODE = 'SESSION_EXPIRED';

/** What the login page says after the app (or Auth) ended the session. */
export const SESSION_ENDED_MESSAGE = 'Your session ended. Please sign in again.';

/** Thrown by the Supabase fetch wrapper while token refresh is backing off after a 429. */
export const REFRESH_RATE_LIMITED_MESSAGE = 'AUTH-002: token refresh is rate limited; backing off';

/**
 * SEC-003 Phase 2b (I2) — the two 403 codes every route can answer when the token is valid but
 * the account may not proceed. Defined here (client-safe) so the browser and the routes share
 * the wording.
 *   ACCOUNT_INACTIVE        → the browser signs the user out with ACCOUNT_INACTIVE_MESSAGE.
 *   PROFILE_NOT_AUTHORIZED  → the browser shows PROFILE_NOT_AUTHORIZED_MESSAGE, session kept.
 */
export const ACCOUNT_INACTIVE_CODE = 'ACCOUNT_INACTIVE';
export const PROFILE_NOT_AUTHORIZED_CODE = 'PROFILE_NOT_AUTHORIZED';
export const ACCOUNT_INACTIVE_MESSAGE = 'Your account is inactive. Contact your administrator.';
export const PROFILE_NOT_AUTHORIZED_MESSAGE =
  'Your account is not set up for this. Contact your administrator.';

/** Window event authFetch raises for a 403 PROFILE_NOT_AUTHORIZED; AuthNoticeBanner shows it. */
export const AUTH_NOTICE_EVENT = 'pugmed:auth-notice';

/**
 * SEC-003 Interim (D7/D8) — 503 from every PHI route until the PHI database is configured
 * (DATABASE_URL set at Connect). Returned after auth and the role check, before any write.
 */
export const PHI_NOT_ENABLED_CODE = 'PHI_NOT_ENABLED';
export const PHI_NOT_ENABLED_MESSAGE =
  'Patient features are not enabled yet. No patient information can be recorded until setup is complete.';
/** What the screens show: the API sentence plus who to ask. */
export const PHI_NOT_ENABLED_NOTICE = `${PHI_NOT_ENABLED_MESSAGE} Contact your administrator.`;
/** Window event authFetch raises for a 503 PHI_NOT_ENABLED; AuthNoticeBanner shows the notice. */
export const PHI_NOT_ENABLED_EVENT = 'pugmed:phi-not-enabled';

/** `inactive` (SEC-003 I2.1): a 403 ACCOUNT_INACTIVE ended the session. */
export type EndReason = 'manual' | 'idle' | 'expired' | 'inactive';

/**
 * Pages that work without a session. Keep in step with `PUBLIC_EXACT` in
 * src/middleware.ts (minus `/api/signout`, which is not a page).
 */
export const SIGNED_OUT_PAGES: readonly string[] = [
  '/login',
  '/register',
  '/auth/callback',
  '/signout',
];

export function isSignedOutPage(pathname: string): boolean {
  return SIGNED_OUT_PAGES.includes(pathname);
}

/**
 * A page load that finds its session already dead sends the user to /login once. If the
 * browser is back here within this window, it does not redirect again: that would mean
 * the session cookie could not be cleared, and a second redirect would only loop.
 */
export const EXPIRED_REDIRECT_GUARD_MS = 30 * 1000;
export const EXPIRED_REDIRECT_KEY = 'pugmed.auth.expiredRedirectAt';

/**
 * Where to land after the session ends. A manual sign-out goes to the plain login page;
 * an automatic end carries `reason=expired` (the login page shows SESSION_ENDED_MESSAGE)
 * and `next=` so the user returns to the page they were on. The login page validates
 * `next` as hostile input (safeNextPath, src/lib/auth/safe-next.ts).
 */
export function loginUrl(reason: EndReason, currentPath?: string): string {
  if (reason === 'manual') return '/login';
  // An inactive account cannot sign back in, so there is no `next` to return to.
  if (reason === 'inactive') return '/login?reason=inactive';
  const params = new URLSearchParams({ reason: 'expired' });
  if (
    currentPath &&
    currentPath.startsWith('/') &&
    !currentPath.startsWith('/login') &&
    !currentPath.startsWith('/signout')
  ) {
    params.set('next', currentPath);
  }
  return `/login?${params.toString()}`;
}
